import { t } from 'app/i18l'
import { useRoutingContext } from 'app/routing_context'
import { HomeStackParamList, ToolsScreenProps } from 'home/stack/types'
import React, { useCallback, useLayoutEffect } from 'react'
import { StatusBoundary } from 'shared/components/status_boundary'
import { onSendSMS } from 'shared/utils'
import { useRecentStoresContext } from 'stores/context'
import { showStoreHeaderDropdown } from 'stores/dropdown'
import { useStores } from 'stores/queries/list'
import ToolsView from 'tools/view'
import { useTextOnlyUsers } from 'users/queries/list'
import { useTools } from '../queries/list'
import { useUpdateReminderDate } from '../queries/update_reminder_date'
import { Tool } from '../types'

export function ToolsScreen({ navigation }: ToolsScreenProps) {
  const { handleNavigation } = useRoutingContext()
  const { data: stores } = useStores(data => data.stores)
  const { activeStoreId, recentStores, setActiveStoreId } =
    useRecentStoresContext()
  const { isLoading, data, error } = useTools(activeStoreId)
  const updateReminderDateMutation = useUpdateReminderDate()

  // Don't fetch text only users if the tool is not present
  const hasTextOnlyUsersTool = !!data?.tools.find(
    tool => tool.name === 'text_only_download_crew'
  )
  const users = useTextOnlyUsers(activeStoreId, hasTextOnlyUsersTool)

  useLayoutEffect(() => {
    showStoreHeaderDropdown(navigation, handleNavigation, {
      activeStoreId,
      recentStores,
      setActiveStoreId,
      stores,
    })
  }, [
    handleNavigation,
    navigation,
    stores,
    activeStoreId,
    recentStores,
    setActiveStoreId,
  ])

  const onPressTool = useCallback(
    async (tool: Tool) => {
      if (tool.name === 'text_only_download_crew') {
        if (!users) {
          return
        }
        const result = await onSendSMS(users, t('downloadNextMessage'))
        if (result === 'sent' || result === 'unknown') {
          await updateReminderDateMutation.mutateAsync({ users })
        }
      } else if (tool.name === 'store_preferences') {
        handleNavigation({
          routeName: 'preferences',
          routeParams: {
            type: 'store_preferences',
            title: tool.title,
          },
        })
      }
      if (tool.route) {
        handleNavigation({
          routeName: tool.route.name as keyof HomeStackParamList,
          routeParams: tool.route.params && { ...tool.route.params },
        })
      }
    },
    [handleNavigation, updateReminderDateMutation, users]
  )

  return (
    <StatusBoundary isLoading={isLoading} error={error}>
      <ToolsView
        tools={data?.tools}
        onPressTool={onPressTool}
        hasTextOnlyUsers={!!users}
      />
    </StatusBoundary>
  )
}
